/**
 * Skeleton — loading placeholders sized to real content.
 *
 * Rule: NEVER use a generic centered spinner for list/table loading. Build
 * the skeleton shape that mirrors the rendered content so layout doesn't
 * shift on load.
 *
 *   <SkeletonRows count={5} height={40}/>      // for tables
 *   <SkeletonCard />                           // for KPIs / drawer panels
 *   <Skeleton width={120} height={16} />       // for inline text bits
 *
 * Use `loading` prop on consumers to swap between Skeleton and real content.
 */

import React from 'react';

const SHIMMER = 'animate-pulse bg-stone-100 rounded';

export function Skeleton({ width, height = 12, circle = false, className = '', style }) {
  return (
    <span
      className={[SHIMMER, circle && '!rounded-full', className].filter(Boolean).join(' ')}
      style={{ display: 'inline-block', width, height, ...style }}
      aria-hidden
    />
  );
}

/** N skeleton rows that match a Table row. Use inside <TBody>. */
export function SkeletonRows({ count = 5, columns = 6, height = 40, withCheckbox = false }) {
  return Array.from({ length: count }, (_, i) => (
    <tr key={i} style={{ height }} className="border-b border-stone-100">
      {withCheckbox && (
        <td className="px-3"><Skeleton width={14} height={14} /></td>
      )}
      {Array.from({ length: columns }, (_, j) => {
        const isFirst = j === 0;
        return (
          <td key={j} className="px-3">
            <Skeleton
              width={isFirst ? '85%' : ['60%', '70%', '50%', '40%'][j % 4]}
              height={10}
            />
          </td>
        );
      })}
    </tr>
  ));
}

/** Skeleton stand-in for a KPI card. */
export function SkeletonCard({ className = '' }) {
  return (
    <div className={['bg-white border border-stone-200 rounded-lg p-3.5 flex flex-col gap-2', className].join(' ')}>
      <div className="flex items-center justify-between">
        <Skeleton width={80}  height={10} />
        <Skeleton width={24} height={24} className="!rounded-md" />
      </div>
      <Skeleton width="60%" height={22} />
      <Skeleton width={100} height={10} />
    </div>
  );
}

/** Long-form drawer / detail skeleton. */
export function SkeletonDetail() {
  return (
    <div className="p-5 space-y-4">
      <Skeleton width="40%" height={18} />
      <Skeleton width="65%" height={14} />
      <div className="pt-2 space-y-2">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="flex items-center justify-between">
            <Skeleton width={100 + i * 8} height={10} />
            <Skeleton width={60 + i * 4} height={10} />
          </div>
        ))}
      </div>
    </div>
  );
}
